import { useEffect, useState, useCallback } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";

// ============ Types & constants ============

export type Category = string;

export const DEFAULT_EXPENSE_CATEGORIES = [
  "Alimentação",
  "Transporte",
  "Lazer",
  "Contas",
  "Saúde",
  "Compras",
  "Outros",
] as const;

export const DEFAULT_INCOME_CATEGORIES = [
  "Salário",
  "Freelance",
  "Bônus",
  "Doações",
  "Investimentos",
  "Reembolso",
] as const;

export const DEFAULT_CATEGORIES = [
  ...DEFAULT_EXPENSE_CATEGORIES,
  ...DEFAULT_INCOME_CATEGORIES,
] as const;

/** Backwards-compatible alias used by existing components. */
export const CATEGORIES: readonly string[] = DEFAULT_CATEGORIES;

export const CATEGORY_COLORS: Record<string, string> = {
  Alimentação: "var(--cat-food)",
  Transporte: "var(--cat-transport)",
  Lazer: "var(--cat-leisure)",
  Contas: "var(--cat-bills)",
  Saúde: "var(--cat-health)",
  Compras: "var(--cat-shopping)",
  Outros: "var(--cat-other)",
  Salário: "var(--cat-salary)",
  Freelance: "var(--cat-freelance)",
  Bônus: "var(--cat-bonus)",
  Doações: "var(--cat-donations)",
  Investimentos: "var(--cat-investments)",
  Reembolso: "var(--cat-refund)",
};

export type Transaction = {
  id: string;
  type: "income" | "expense";
  amount: number;
  category: Category;
  description: string;
  date: string; // ISO
};

export type Goal = {
  id: string;
  name: string;
  target: number;
  current: number;
};

export type SubscriptionPlan = "free" | "premium";
export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "canceled"
  | "past_due"
  | "paused";

export type Subscription = {
  id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  priceId: string | null;
};

export type CustomCategory = {
  id: string;
  name: string;
  type: "income" | "expense";
  color: string | null;
};

export const PLAN_LIMITS = {
  free: { goals: 3, transactionsPerMonth: 30, customCategories: 2 },
  premium: { goals: Infinity, transactionsPerMonth: Infinity, customCategories: Infinity },
} as const;

// ============ Module-level cache ============
// Caches keyed by userId so that navigating between pages does not re-trigger
// the loading skeleton every time the same hook remounts. The cache is
// hydrated by the first fetch and kept in sync by mutations.

const txCache = new Map<string, Transaction[]>();
const goalsCache = new Map<string, Goal[]>();
const subscriptionCache = new Map<string, Subscription>();
const categoriesCache = new Map<string, CustomCategory[]>();

// ---- Cross-hook subscribers ----------------------------------------------
// When one component mutates (add/remove/update) we want every other mounted
// hook for the same userId to re-render with the new value. Each cache has
// its own set of listeners keyed by userId.

type Listener<T> = (value: T) => void;
const txListeners = new Map<string, Set<Listener<Transaction[]>>>();
const goalsListeners = new Map<string, Set<Listener<Goal[]>>>();
const categoriesListeners = new Map<string, Set<Listener<CustomCategory[]>>>();

// ---- Pending-delete registry for transactions ----------------------------
// IDs scheduled for deletion within the undo window. Stored at module level
// so timers survive route changes (navigating away from Transações must NOT
// cancel the pending delete). Visible transactions are filtered against this
// set so every consumer (dashboard, charts, filter dropdowns, lists) reflects
// the optimistic removal immediately, and the eventual real delete updates
// every subscriber via emit().

type PendingTxEntry = { userId: string; timer: ReturnType<typeof setTimeout> };
const pendingTxDeletes = new Map<string, PendingTxEntry>();
const pendingTxListeners = new Set<() => void>();

function notifyPendingTx() {
  for (const fn of pendingTxListeners) fn();
}

export function getPendingTxIds(): ReadonlySet<string> {
  return new Set(pendingTxDeletes.keys());
}

function subscribePendingTx(fn: () => void) {
  pendingTxListeners.add(fn);
  return () => {
    pendingTxListeners.delete(fn);
  };
}

function subscribe<T>(
  map: Map<string, Set<Listener<T>>>,
  userId: string,
  fn: Listener<T>
) {
  let set = map.get(userId);
  if (!set) {
    set = new Set();
    map.set(userId, set);
  }
  set.add(fn);
  return () => {
    set!.delete(fn);
  };
}

function emit<T>(map: Map<string, Set<Listener<T>>>, userId: string, value: T) {
  const set = map.get(userId);
  if (!set) return;
  for (const fn of set) fn(value);
}

// ============ Helpers ============

export const formatBRL = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const sameMonth = (iso: string, ref = new Date()) => {
  const d = new Date(iso);
  return d.getMonth() === ref.getMonth() && d.getFullYear() === ref.getFullYear();
};

// ============ Auth hook ============

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up listener FIRST
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
    });
    // Then load existing session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    return supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: name ? { display_name: name } : undefined,
      },
    });
  }, []);

  const signIn = useCallback((email: string, password: string) => {
    return supabase.auth.signInWithPassword({ email, password });
  }, []);

  const signOut = useCallback(() => supabase.auth.signOut(), []);

  const resetPasswordForEmail = useCallback((email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    return supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });
  }, []);

  const updatePassword = useCallback((password: string) => {
    return supabase.auth.updateUser({ password });
  }, []);

  const displayName =
    (user?.user_metadata?.display_name as string | undefined) ||
    user?.email?.split("@")[0] ||
    "";

  return { session, user, loading, displayName, signUp, signIn, signOut, resetPasswordForEmail, updatePassword };
}

// ============ Transactions hook ============

type TxRow = {
  id: string;
  type: string;
  amount: number;
  category: string;
  description: string;
  date: string;
};

const mapTx = (r: TxRow): Transaction => ({
  id: r.id,
  type: r.type as "income" | "expense",
  amount: Number(r.amount),
  category: r.category as Category,
  description: r.description,
  date: r.date,
});

export function useTransactions(userId: string | undefined) {
  const cached = userId ? txCache.get(userId) : undefined;
  const [rawTransactions, setTransactions] = useState<Transaction[]>(cached ?? []);
  // Only show the loading state on the very first fetch for this user.
  // Subsequent mounts reuse the cache and refresh silently in the background.
  const [loading, setLoading] = useState(cached === undefined);
  // Tick that bumps whenever the pending-delete set changes, so the derived
  // visible transactions list re-computes.
  const [, setPendingTick] = useState(0);

  // Stay in sync with mutations from other components.
  useEffect(() => {
    if (!userId) return;
    return subscribe(txListeners, userId, (next) => setTransactions(next));
  }, [userId]);

  // Re-render whenever a delete is scheduled, undone, or commits.
  useEffect(() => {
    return subscribePendingTx(() => setPendingTick((t) => t + 1));
  }, []);

  const refresh = useCallback(async () => {
    if (!userId) {
      setTransactions([]);
      setLoading(false);
      return;
    }
    if (txCache.get(userId) === undefined) setLoading(true);
    const { data, error } = await supabase
      .from("transactions")
      .select("id,type,amount,category,description,date")
      .order("date", { ascending: false });
    if (!error && data) {
      const mapped = data.map(mapTx);
      txCache.set(userId, mapped);
      setTransactions(mapped);
      emit(txListeners, userId, mapped);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime: refetch when this user's transactions change anywhere
  // (other tabs, other devices, or another route in this tab).
  useEffect(() => {
    if (!userId) return;
    const channelName = `transactions:${userId}:${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "transactions",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          refresh();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refresh]);

  const addTransaction = useCallback(
    async (t: Omit<Transaction, "id">) => {
      if (!userId) return;
      const { data, error } = await supabase
        .from("transactions")
        .insert({
          user_id: userId,
          type: t.type,
          amount: t.amount,
          category: t.category,
          description: t.description,
          date: t.date,
        })
        .select("id,type,amount,category,description,date")
        .single();
      if (!error && data) {
        setTransactions((prev) => {
          const next = [mapTx(data), ...prev];
          txCache.set(userId, next);
          emit(txListeners, userId, next);
          return next;
        });
      }
      return { error };
    },
    [userId]
  );

  const removeTransaction = useCallback(
    async (id: string) => {
      const { error } = await supabase.from("transactions").delete().eq("id", id);
      if (!error) {
        setTransactions((prev) => {
          const next = prev.filter((t) => t.id !== id);
          if (userId) txCache.set(userId, next);
          if (userId) emit(txListeners, userId, next);
          return next;
        });
      }
      return { error };
    },
    [userId]
  );

  const addTransactionsBulk = useCallback(
    async (rows: Array<Omit<Transaction, "id">>) => {
      if (!userId || rows.length === 0) return { error: null, inserted: 0 };
      const inserted: Transaction[] = [];
      let firstError: unknown = null;
      // Insert one-by-one so the per-row plan-limit trigger fires correctly
      // and we can report which rows failed.
      for (const t of rows) {
        const { data, error } = await supabase
          .from("transactions")
          .insert({
            user_id: userId,
            type: t.type,
            amount: t.amount,
            category: t.category,
            description: t.description,
            date: t.date,
          })
          .select("id,type,amount,category,description,date")
          .single();
        if (error) {
          if (!firstError) firstError = error;
          // Stop on limit error so we don't spam failures.
          const msg = (error as { message?: string }).message ?? "";
          if (msg.includes("TRANSACTIONS_MONTHLY_LIMIT_REACHED")) break;
          continue;
        }
        if (data) inserted.push(mapTx(data));
      }
      if (inserted.length > 0) {
        setTransactions((prev) => {
          const next = [...inserted, ...prev];
          txCache.set(userId, next);
          emit(txListeners, userId, next);
          return next;
        });
      }
      return { error: firstError, inserted: inserted.length };
    },
    [userId]
  );

  // Schedule a delayed delete with a cancelable undo window. Survives route
  // changes because the timer lives in the module-level registry.
  const scheduleDeleteTransaction = useCallback(
    (
      id: string,
      opts?: { delayMs?: number; onCommit?: (err: unknown) => void }
    ) => {
      if (!userId) return () => {};
      // Already pending? Ignore double trigger.
      if (pendingTxDeletes.has(id)) return () => cancelDeleteTransaction(id);

      const delay = opts?.delayMs ?? 5000;
      const timer = setTimeout(async () => {
        pendingTxDeletes.delete(id);
        notifyPendingTx();
        const { error } = await supabase.from("transactions").delete().eq("id", id);
        if (!error) {
          const current = txCache.get(userId) ?? [];
          const next = current.filter((t) => t.id !== id);
          txCache.set(userId, next);
          emit(txListeners, userId, next);
        }
        opts?.onCommit?.(error);
      }, delay);

      pendingTxDeletes.set(id, { userId, timer });
      notifyPendingTx();
      return () => cancelDeleteTransaction(id);
    },
    [userId]
  );

  const visibleTransactions = rawTransactions.filter(
    (t) => !pendingTxDeletes.has(t.id)
  );

  return {
    transactions: visibleTransactions,
    loading,
    refresh,
    addTransaction,
    addTransactionsBulk,
    removeTransaction,
    scheduleDeleteTransaction,
  };
}

export function cancelDeleteTransaction(id: string) {
  const entry = pendingTxDeletes.get(id);
  if (!entry) return false;
  clearTimeout(entry.timer);
  pendingTxDeletes.delete(id);
  notifyPendingTx();
  return true;
}

// ============ Goals hook ============

type GoalRow = {
  id: string;
  name: string;
  target: number;
  current: number;
};

const mapGoal = (r: GoalRow): Goal => ({
  id: r.id,
  name: r.name,
  target: Number(r.target),
  current: Number(r.current),
});

export function useGoals(userId: string | undefined) {
  const cached = userId ? goalsCache.get(userId) : undefined;
  const [goals, setGoals] = useState<Goal[]>(cached ?? []);
  const [loading, setLoading] = useState(cached === undefined);

  useEffect(() => {
    if (!userId) return;
    return subscribe(goalsListeners, userId, (next) => setGoals(next));
  }, [userId]);

  const refresh = useCallback(async () => {
    if (!userId) {
      setGoals([]);
      setLoading(false);
      return;
    }
    if (goalsCache.get(userId) === undefined) setLoading(true);
    const { data, error } = await supabase
      .from("goals")
      .select("id,name,target,current")
      .order("created_at", { ascending: true });
    if (!error && data) {
      const mapped = data.map(mapGoal);
      goalsCache.set(userId, mapped);
      setGoals(mapped);
      emit(goalsListeners, userId, mapped);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addGoal = useCallback(
    async (g: Omit<Goal, "id">) => {
      if (!userId) return { error: null };
      const { data, error } = await supabase
        .from("goals")
        .insert({ user_id: userId, name: g.name, target: g.target, current: g.current })
        .select("id,name,target,current")
        .single();
      if (!error && data) {
        setGoals((prev) => {
          const next = [...prev, mapGoal(data)];
          goalsCache.set(userId, next);
          emit(goalsListeners, userId, next);
          return next;
        });
      }
      return { error };
    },
    [userId]
  );

  const updateGoal = useCallback(
    async (id: string, patch: Partial<Goal>) => {
      const { data, error } = await supabase
        .from("goals")
        .update({
          ...(patch.name !== undefined && { name: patch.name }),
          ...(patch.target !== undefined && { target: patch.target }),
          ...(patch.current !== undefined && { current: patch.current }),
        })
        .eq("id", id)
        .select("id,name,target,current")
        .single();
      if (!error && data) {
        // Fire-and-forget: notify backend in case the goal was just reached
        if (data.current >= data.target && data.target > 0) {
          checkGoalReached(data.id);
        }
        setGoals((prev) => {
          const next = prev.map((g) => (g.id === id ? mapGoal(data) : g));
          if (userId) goalsCache.set(userId, next);
          if (userId) emit(goalsListeners, userId, next);
          return next;
        });
      }
      return { error };
    },
    [userId]
  );

  const removeGoal = useCallback(
    async (id: string) => {
      const { error } = await supabase.from("goals").delete().eq("id", id);
      if (!error) {
        setGoals((prev) => {
          const next = prev.filter((g) => g.id !== id);
          if (userId) goalsCache.set(userId, next);
          if (userId) emit(goalsListeners, userId, next);
          return next;
        });
      }
    },
    [userId]
  );

  return { goals, loading, refresh, addGoal, updateGoal, removeGoal };
}

// ============ Subscription hook ============

type SubscriptionRow = {
  id: string;
  plan: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  price_id: string | null;
};

const mapSubscription = (r: SubscriptionRow): Subscription => ({
  id: r.id,
  plan: r.plan as SubscriptionPlan,
  status: r.status as SubscriptionStatus,
  currentPeriodEnd: r.current_period_end,
  cancelAtPeriodEnd: !!r.cancel_at_period_end,
  priceId: r.price_id,
});

export function useSubscription(userId: string | undefined) {
  const cached = userId ? subscriptionCache.get(userId) : undefined;
  const [subscription, setSubscription] = useState<Subscription | null>(cached ?? null);
  const [loading, setLoading] = useState(cached === undefined);

  const refresh = useCallback(async () => {
    if (!userId) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    if (subscriptionCache.get(userId) === undefined) setLoading(true);
    const env = getPaddleEnvironment();
    const { data, error } = await supabase
      .from("subscriptions")
      .select("id,plan,status,current_period_end,cancel_at_period_end,price_id")
      .eq("user_id", userId)
      .eq("environment", env)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error && data) {
      const mapped = mapSubscription(data as SubscriptionRow);
      subscriptionCache.set(userId, mapped);
      setSubscription(mapped);
    } else {
      // No row = free plan (a Paddle row only exists after a real purchase).
      const fallback: Subscription = {
        id: "",
        plan: "free",
        status: "active",
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
        priceId: null,
      };
      subscriptionCache.set(userId, fallback);
      setSubscription(fallback);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Active premium = plan premium AND status is active/trialing/past_due (with future period end),
  // OR canceled but still within paid period (grace period until current_period_end).
  const now = Date.now();
  const periodEndMs = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).getTime()
    : null;
  const isPremium =
    subscription?.plan === "premium" &&
    (((["active", "trialing", "past_due"] as const).includes(
      (subscription.status ?? "active") as "active" | "trialing" | "past_due"
    ) &&
      (periodEndMs === null || periodEndMs > now)) ||
      (subscription.status === "canceled" && periodEndMs !== null && periodEndMs > now));
  const plan: SubscriptionPlan = isPremium ? "premium" : "free";

  // Realtime: refetch when this user's subscription rows change.
  useEffect(() => {
    if (!userId) return;
    // Unique channel name per effect instance to avoid "cannot add postgres_changes
    // callbacks ... after subscribe()" when React Strict Mode (or fast refresh)
    // remounts and the previous channel is still tearing down.
    const channelName = `subscriptions:${userId}:${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Invalidate cache so the env-filtered query runs again.
          subscriptionCache.delete(userId);
          refresh();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refresh]);

  const goalsLimit = PLAN_LIMITS[plan].goals;
  const transactionsMonthlyLimit = PLAN_LIMITS[plan].transactionsPerMonth;
  const customCategoriesLimit = PLAN_LIMITS[plan].customCategories;

  return {
    subscription,
    plan,
    isPremium,
    goalsLimit,
    transactionsMonthlyLimit,
    customCategoriesLimit,
    loading,
    refresh,
  };
}

// ============ Categories hook ============

type CategoryRow = {
  id: string;
  name: string;
  type: string;
  color: string | null;
};

const mapCategory = (r: CategoryRow): CustomCategory => ({
  id: r.id,
  name: r.name,
  type: r.type as "income" | "expense",
  color: r.color,
});

export function useCategories(userId: string | undefined) {
  const cached = userId ? categoriesCache.get(userId) : undefined;
  const [categories, setCategories] = useState<CustomCategory[]>(cached ?? []);
  const [loading, setLoading] = useState(cached === undefined);

  useEffect(() => {
    if (!userId) return;
    return subscribe(categoriesListeners, userId, (next) => setCategories(next));
  }, [userId]);

  const refresh = useCallback(async () => {
    if (!userId) {
      setCategories([]);
      setLoading(false);
      return;
    }
    if (categoriesCache.get(userId) === undefined) setLoading(true);
    const { data, error } = await supabase
      .from("categories")
      .select("id,name,type,color")
      .order("created_at", { ascending: true });
    if (!error && data) {
      const mapped = (data as CategoryRow[]).map(mapCategory);
      categoriesCache.set(userId, mapped);
      setCategories(mapped);
      emit(categoriesListeners, userId, mapped);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addCategory = useCallback(
    async (c: { name: string; type: "income" | "expense"; color?: string | null }) => {
      if (!userId) return { error: null };
      const { data, error } = await supabase
        .from("categories")
        .insert({
          user_id: userId,
          name: c.name.trim(),
          type: c.type,
          color: c.color ?? null,
        })
        .select("id,name,type,color")
        .single();
      if (!error && data) {
        setCategories((prev) => {
          const next = [...prev, mapCategory(data as CategoryRow)];
          categoriesCache.set(userId, next);
          emit(categoriesListeners, userId, next);
          return next;
        });
      }
      return { error };
    },
    [userId]
  );

  const removeCategory = useCallback(
    async (id: string) => {
      const { error } = await supabase.from("categories").delete().eq("id", id);
      if (!error) {
        setCategories((prev) => {
          const next = prev.filter((c) => c.id !== id);
          if (userId) categoriesCache.set(userId, next);
          if (userId) emit(categoriesListeners, userId, next);
          return next;
        });
      }
      return { error };
    },
    [userId]
  );

  return { categories, loading, refresh, addCategory, removeCategory };
}

// ============ Recurring transactions hook ============

export type RecurringFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly'

export function useRecurring(userId: string | undefined) {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!userId) { setItems([]); setLoading(false); return }
    const { data } = await supabase
      .from('recurring_transactions')
      .select('*')
      .order('created_at', { ascending: false })
    setItems(data ?? [])
    setLoading(false)
  }, [userId])

  useEffect(() => { refresh() }, [refresh])

  const addRule = useCallback(async (rule: {
    type: 'income' | 'expense'
    amount: number
    category: string
    description: string
    frequency: RecurringFrequency
    start_date: string // YYYY-MM-DD
  }) => {
    if (!userId) return { error: null }
    // next_run = today + 1 period (today already inserted as a regular tx)
    const next = new Date(rule.start_date + 'T12:00:00')
    switch (rule.frequency) {
      case 'daily': next.setDate(next.getDate() + 1); break
      case 'weekly': next.setDate(next.getDate() + 7); break
      case 'monthly': next.setMonth(next.getMonth() + 1); break
      case 'yearly': next.setFullYear(next.getFullYear() + 1); break
    }
    const nextKey = next.toISOString().slice(0, 10)
    const { error } = await supabase.from('recurring_transactions').insert({
      user_id: userId, ...rule, next_run_date: nextKey,
    })
    if (!error) refresh()
    return { error }
  }, [userId, refresh])

  const toggleRule = useCallback(async (id: string, active: boolean) => {
    await supabase.from('recurring_transactions').update({ active }).eq('id', id)
    refresh()
  }, [refresh])

  const removeRule = useCallback(async (id: string) => {
    await supabase.from('recurring_transactions').delete().eq('id', id)
    refresh()
  }, [refresh])

  return { items, loading, refresh, addRule, toggleRule, removeRule }
}

// ============ Email preferences hook ============

export type EmailPreferences = {
  weekly_summary: boolean
  monthly_summary: boolean
  goal_alerts: boolean
  expense_reminders: boolean
  reminder_days_inactive: number
}

const DEFAULT_EMAIL_PREFS: EmailPreferences = {
  weekly_summary: true,
  monthly_summary: true,
  goal_alerts: true,
  expense_reminders: true,
  reminder_days_inactive: 7,
}

export function useEmailPreferences(userId: string | undefined) {
  const [prefs, setPrefs] = useState<EmailPreferences>(DEFAULT_EMAIL_PREFS)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) { setLoading(false); return }
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('email_preferences')
        .select('weekly_summary,monthly_summary,goal_alerts,expense_reminders,reminder_days_inactive')
        .eq('user_id', userId)
        .maybeSingle()
      if (cancelled) return
      if (data) setPrefs(data as EmailPreferences)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [userId])

  const update = useCallback(async (patch: Partial<EmailPreferences>) => {
    if (!userId) return
    const next = { ...prefs, ...patch }
    setPrefs(next)
    await supabase
      .from('email_preferences')
      .upsert({ user_id: userId, ...next }, { onConflict: 'user_id' })
  }, [userId, prefs])

  return { prefs, loading, update }
}

/** Notify backend that a goal may have been reached, sends email if so. */
export async function checkGoalReached(goalId: string) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return
  try {
    await fetch('/api/public/hooks/check-goal', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ goalId }),
    })
  } catch {
    // best-effort
  }
}
